import {ViewIdentifiable} from "../../models/common/ViewSettings";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {ApiReturnType, getUserViewSettingsApiRequest} from "../../models/common/ApiReturnType";
import {AppState} from "../../reducers/RootReducer";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {eventNotificationError, eventNotificationSuccess} from "../common/EventNotificationActions";
import {
    ProjectStagePhaseTemplate,
    ProjectStagePhaseTemplateId,
    ProjectStagePhaseTemplateModule,
    ProjectStagePhaseTemplateProp
} from "../../models/projects/ProjectStagePhaseTemplate";
import {ofType, StateObservable} from "redux-observable";
import {filter, map, mergeMap, catchError} from "rxjs/operators";
import {ajax} from "rxjs/ajax";
import {Observable, of} from "rxjs";
import {Identifier} from "../../models/common/Identifier";
import {RouteConfig, RouteMethods} from "../../config/RouteConfig";
import {sendUserToPage} from "../common/UserViewSettingsActions";

/**
 * base API path to interact with phase templates
 *
 * @see ProjectStagePhaseTemplateModule
 */
const basePath = () => `${ APP_CONFIG.baseWeb }projects/stage/phase/templates/`;

/**
 * api path including the phase template identifier
 *
 * @param templateId the unique identifier of the phase template
 */
const pathWithId = ( templateId: number ) => `${ basePath() }${ templateId }`;

/** union of all phase template action types */
export type ProjectStagePhaseTemplateActionTypes =
    FetchProjectStagePhaseTemplatesActionTypes
    | FetchProjectStagePhaseTemplateByIdActionTypes
    | CreateProjectStagePhaseTemplateActionTypes
    | UpdateProjectStagePhaseTemplateActionTypes
    | DeleteProjectStagePhaseTemplateActionTypes
    | RestoreProjectStagePhaseTemplateActionTypes

/**
 * FETCH PHASE TEMPLATES BY USER VIEW SETTINGS
 *
 * user view settings will automatically be retrieved from redux store
 */
// TYPESCRIPT REDUX ACTION TYPES
export interface FetchProjectStagePhaseTemplatesAction
    extends ViewIdentifiable, ReduxAction<typeof FETCH_PROJECT_STAGE_PHASE_TEMPLATES> {}
export interface FetchProjectStagePhaseTemplatesSuccessAction
    extends ViewIdentifiable, ReduxAction<typeof FETCH_PROJECT_STAGE_PHASE_TEMPLATES_SUCCESS> {
    payload: ApiReturnType< ProjectStagePhaseTemplate[] >
}
export interface FetchProjectStagePhaseTemplatesErrorAction
    extends ViewIdentifiable, ErrorAction<typeof FETCH_PROJECT_STAGE_PHASE_TEMPLATES_ERROR> {}

export type FetchProjectStagePhaseTemplatesActionTypes =
    FetchProjectStagePhaseTemplatesAction
    | FetchProjectStagePhaseTemplatesSuccessAction
    | FetchProjectStagePhaseTemplatesErrorAction

// REDUX ACTION TYPE CONSTANTS
export const FETCH_PROJECT_STAGE_PHASE_TEMPLATES = "FETCH_PROJECT_STAGE_PHASE_TEMPLATES";
export const FETCH_PROJECT_STAGE_PHASE_TEMPLATES_SUCCESS = "FETCH_PROJECT_STAGE_PHASE_TEMPLATES_SUCCESS";
export const FETCH_PROJECT_STAGE_PHASE_TEMPLATES_ERROR = "FETCH_PROJECT_STAGE_PHASE_TEMPLATES_ERROR";

// REDUX ACTION ACCESSORS FUNCTIONS
export function fetchProjectStagePhaseTemplates( viewIdentifier: string ): FetchProjectStagePhaseTemplatesAction {
    return { type: FETCH_PROJECT_STAGE_PHASE_TEMPLATES, viewIdentifier };
}
export function fetchProjectStagePhaseTemplatesSuccess(
    viewIdentifier: string,
    payload: ApiReturnType< ProjectStagePhaseTemplate[] >
): FetchProjectStagePhaseTemplatesSuccessAction {
    return { type: FETCH_PROJECT_STAGE_PHASE_TEMPLATES_SUCCESS, viewIdentifier, payload };
}
export function fetchProjectStagePhaseTemplatesError(
    viewIdentifier: string,
    error: Error
): FetchProjectStagePhaseTemplatesErrorAction {
    return { type: FETCH_PROJECT_STAGE_PHASE_TEMPLATES_ERROR, viewIdentifier, error };
}

// REDUX OBSERVABLE EPIC
export function fetchProjectStagePhaseTemplatesEpic(
    action$: Observable< FetchProjectStagePhaseTemplatesAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_PROJECT_STAGE_PHASE_TEMPLATES ),
        filter( action => action.viewIdentifier === ProjectStagePhaseTemplateModule.identifier ),
        mergeMap( action => {
            const request = getUserViewSettingsApiRequest(
                basePath(),
                ProjectStagePhaseTemplateModule.identifier,
                store$
            );

            return ajax( APP_CONFIG.getRequestObject( request.path ) ).pipe(
                map( response => fetchProjectStagePhaseTemplatesSuccess(
                    action.viewIdentifier,
                    response.response as ApiReturnType< ProjectStagePhaseTemplate[] >
                ) ),
                catchError( error => of(
                    fetchProjectStagePhaseTemplatesError( action.viewIdentifier, error ),
                    eventNotificationError(
                        error,
                        store$,
                        ProjectStagePhaseTemplateModule.identifier,
                        "fetchProjectStagePhaseTemplates"
                    )
                ) )
            );
        })
    );
}

/**
 * FETCH SINGLE PHASE TEMPLATE
 */
export interface FetchProjectStagePhaseTemplateByIdAction
    extends ProjectStagePhaseTemplateId, ReduxAction<typeof FETCH_PROJECT_STAGE_PHASE_TEMPLATE_BY_ID> {}
export interface FetchProjectStagePhaseTemplateByIdSuccessAction
    extends ProjectStagePhaseTemplateProp, ReduxAction<typeof FETCH_PROJECT_STAGE_PHASE_TEMPLATE_BY_ID_SUCCESS> {}
export interface FetchProjectStagePhaseTemplateByIdErrorAction
    extends ProjectStagePhaseTemplateId, ErrorAction<typeof FETCH_PROJECT_STAGE_PHASE_TEMPLATE_BY_ID_ERROR> {}

export type FetchProjectStagePhaseTemplateByIdActionTypes =
    FetchProjectStagePhaseTemplateByIdAction
    | FetchProjectStagePhaseTemplateByIdSuccessAction
    | FetchProjectStagePhaseTemplateByIdErrorAction

// REDUX ACTION TYPE CONSTANTS
export const FETCH_PROJECT_STAGE_PHASE_TEMPLATE_BY_ID = "FETCH_PROJECT_STAGE_PHASE_TEMPLATE_BY_ID";
export const FETCH_PROJECT_STAGE_PHASE_TEMPLATE_BY_ID_SUCCESS = "FETCH_PROJECT_STAGE_PHASE_TEMPLATE_BY_ID_SUCCESS";
export const FETCH_PROJECT_STAGE_PHASE_TEMPLATE_BY_ID_ERROR = "FETCH_PROJECT_STAGE_PHASE_TEMPLATE_BY_ID_ERROR";

// REDUX ACTION ACCESSORS FUNCTIONS
export function fetchProjectStagePhaseTemplateById(
    phaseTemplateId: Identifier<number>
): FetchProjectStagePhaseTemplateByIdAction {
    return { type: FETCH_PROJECT_STAGE_PHASE_TEMPLATE_BY_ID, phaseTemplateId };
}
export function fetchProjectStagePhaseTemplateByIdSuccess(
    phaseTemplate: ProjectStagePhaseTemplate
): FetchProjectStagePhaseTemplateByIdSuccessAction {
    return { type: FETCH_PROJECT_STAGE_PHASE_TEMPLATE_BY_ID_SUCCESS, phaseTemplate };
}
export function fetchProjectStagePhaseTemplateByIdError(
    phaseTemplateId: Identifier<number>,
    error: Error
): FetchProjectStagePhaseTemplateByIdErrorAction {
    return { type: FETCH_PROJECT_STAGE_PHASE_TEMPLATE_BY_ID_ERROR, phaseTemplateId, error };
}

// REDUX OBSERVABLE EPIC
export function fetchProjectStagePhaseTemplateByIdEpic(
    action$: Observable< FetchProjectStagePhaseTemplateByIdAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_PROJECT_STAGE_PHASE_TEMPLATE_BY_ID ),
        mergeMap( action =>
            ajax(
                APP_CONFIG.getRequestObject( pathWithId( action.phaseTemplateId.rawValue ) )
            ).pipe(
                map( response => fetchProjectStagePhaseTemplateByIdSuccess(
                    response.response as ProjectStagePhaseTemplate
                ) ),
                catchError( error => of(
                    fetchProjectStagePhaseTemplateByIdError( action.phaseTemplateId, error ),
                    eventNotificationError(
                        error,
                        store$,
                        ProjectStagePhaseTemplateModule.identifier,
                        "fetchProjectStagePhaseTemplateById"
                    )
                ) )
            )
        )
    );
}

/**
 * CREATE PHASE TEMPLATE
 *
 * important:
 * when an active template with the same name already exists, the backend
 * does not create a duplicate and returns the existing template instead
 */
export interface CreateProjectStagePhaseTemplateAction
    extends ProjectStagePhaseTemplateProp, ReduxAction<typeof CREATE_PROJECT_STAGE_PHASE_TEMPLATE> {}
export interface CreateProjectStagePhaseTemplateSuccessAction
    extends ProjectStagePhaseTemplateId, ReduxAction<typeof CREATE_PROJECT_STAGE_PHASE_TEMPLATE_SUCCESS> {}
export interface CreateProjectStagePhaseTemplateErrorAction
    extends ErrorAction<typeof CREATE_PROJECT_STAGE_PHASE_TEMPLATE_ERROR> {}

export type CreateProjectStagePhaseTemplateActionTypes =
    CreateProjectStagePhaseTemplateAction
    | CreateProjectStagePhaseTemplateSuccessAction
    | CreateProjectStagePhaseTemplateErrorAction

// REDUX ACTION TYPE CONSTANTS
export const CREATE_PROJECT_STAGE_PHASE_TEMPLATE = "CREATE_PROJECT_STAGE_PHASE_TEMPLATE";
export const CREATE_PROJECT_STAGE_PHASE_TEMPLATE_SUCCESS = "CREATE_PROJECT_STAGE_PHASE_TEMPLATE_SUCCESS";
export const CREATE_PROJECT_STAGE_PHASE_TEMPLATE_ERROR = "CREATE_PROJECT_STAGE_PHASE_TEMPLATE_ERROR";

// REDUX ACTION ACCESSORS FUNCTIONS
export function createProjectStagePhaseTemplate(
    phaseTemplate: ProjectStagePhaseTemplate
): CreateProjectStagePhaseTemplateAction {
    return { type: CREATE_PROJECT_STAGE_PHASE_TEMPLATE, phaseTemplate };
}
export function createProjectStagePhaseTemplateSuccess(
    phaseTemplateId: Identifier<number>
): CreateProjectStagePhaseTemplateSuccessAction {
    return { type: CREATE_PROJECT_STAGE_PHASE_TEMPLATE_SUCCESS, phaseTemplateId };
}
export function createProjectStagePhaseTemplateError(
    error: Error
): CreateProjectStagePhaseTemplateErrorAction {
    return { type: CREATE_PROJECT_STAGE_PHASE_TEMPLATE_ERROR, error };
}

// REDUX OBSERVABLE EPIC
export function createProjectStagePhaseTemplateEpic(
    action$: Observable< CreateProjectStagePhaseTemplateAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( CREATE_PROJECT_STAGE_PHASE_TEMPLATE ),
        mergeMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    basePath(),
                    RouteMethods.post,
                    action.phaseTemplate
                )
            ).pipe(
                mergeMap( _ => of(
                    createProjectStagePhaseTemplateSuccess( action.phaseTemplate.phaseTemplateId ),
                    eventNotificationSuccess(),
                    sendUserToPage(
                        RouteConfig.phaseTemplates.routes.main.path,
                        ProjectStagePhaseTemplateModule.identifier
                    ),
                    fetchProjectStagePhaseTemplates( ProjectStagePhaseTemplateModule.identifier )
                ) ),
                catchError( error => of(
                    createProjectStagePhaseTemplateError( error ),
                    eventNotificationError(
                        error,
                        store$,
                        ProjectStagePhaseTemplateModule.identifier,
                        "createProjectStagePhaseTemplate"
                    )
                ) )
            )
        )
    );
}

/**
 * UPDATE PHASE TEMPLATE
 *
 * updates only name, shorthand and description. existing phases/tasks keep
 * their own copies made at creation time and are not affected
 */
export interface UpdateProjectStagePhaseTemplateAction
    extends ProjectStagePhaseTemplateProp, ReduxAction<typeof UPDATE_PROJECT_STAGE_PHASE_TEMPLATE> {}
export interface UpdateProjectStagePhaseTemplateSuccessAction
    extends ProjectStagePhaseTemplateId, ReduxAction<typeof UPDATE_PROJECT_STAGE_PHASE_TEMPLATE_SUCCESS> {}
export interface UpdateProjectStagePhaseTemplateErrorAction
    extends ErrorAction<typeof UPDATE_PROJECT_STAGE_PHASE_TEMPLATE_ERROR> {}

export type UpdateProjectStagePhaseTemplateActionTypes =
    UpdateProjectStagePhaseTemplateAction
    | UpdateProjectStagePhaseTemplateSuccessAction
    | UpdateProjectStagePhaseTemplateErrorAction

// REDUX ACTION TYPE CONSTANTS
export const UPDATE_PROJECT_STAGE_PHASE_TEMPLATE = "UPDATE_PROJECT_STAGE_PHASE_TEMPLATE";
export const UPDATE_PROJECT_STAGE_PHASE_TEMPLATE_SUCCESS = "UPDATE_PROJECT_STAGE_PHASE_TEMPLATE_SUCCESS";
export const UPDATE_PROJECT_STAGE_PHASE_TEMPLATE_ERROR = "UPDATE_PROJECT_STAGE_PHASE_TEMPLATE_ERROR";

// REDUX ACTION ACCESSORS FUNCTIONS
export function updateProjectStagePhaseTemplate(
    phaseTemplate: ProjectStagePhaseTemplate
): UpdateProjectStagePhaseTemplateAction {
    return { type: UPDATE_PROJECT_STAGE_PHASE_TEMPLATE, phaseTemplate };
}
export function updateProjectStagePhaseTemplateSuccess(
    phaseTemplateId: Identifier<number>
): UpdateProjectStagePhaseTemplateSuccessAction {
    return { type: UPDATE_PROJECT_STAGE_PHASE_TEMPLATE_SUCCESS, phaseTemplateId };
}
export function updateProjectStagePhaseTemplateError(
    error: Error
): UpdateProjectStagePhaseTemplateErrorAction {
    return { type: UPDATE_PROJECT_STAGE_PHASE_TEMPLATE_ERROR, error };
}

// REDUX OBSERVABLE EPIC
export function updateProjectStagePhaseTemplateEpic(
    action$: Observable< UpdateProjectStagePhaseTemplateAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( UPDATE_PROJECT_STAGE_PHASE_TEMPLATE ),
        mergeMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    pathWithId( action.phaseTemplate.phaseTemplateId.rawValue ),
                    RouteMethods.put,
                    action.phaseTemplate
                )
            ).pipe(
                mergeMap( _ => of(
                    updateProjectStagePhaseTemplateSuccess( action.phaseTemplate.phaseTemplateId ),
                    eventNotificationSuccess(),
                    sendUserToPage(
                        RouteConfig.phaseTemplates.routes.main.path,
                        ProjectStagePhaseTemplateModule.identifier
                    ),
                    fetchProjectStagePhaseTemplateById( action.phaseTemplate.phaseTemplateId ),
                    fetchProjectStagePhaseTemplates( ProjectStagePhaseTemplateModule.identifier )
                ) ),
                catchError( error => of(
                    updateProjectStagePhaseTemplateError( error ),
                    eventNotificationError(
                        error,
                        store$,
                        ProjectStagePhaseTemplateModule.identifier,
                        "updateProjectStagePhaseTemplate"
                    )
                ) )
            )
        )
    );
}

/**
 * DELETE PHASE TEMPLATE
 *
 * soft delete only. references from phases and tasks stay intact, the template
 * just disappears from the select options. deleted templates can be restored
 */
export interface DeleteProjectStagePhaseTemplateAction
    extends ProjectStagePhaseTemplateId, ReduxAction<typeof DELETE_PROJECT_STAGE_PHASE_TEMPLATE> {}
export interface DeleteProjectStagePhaseTemplateSuccessAction
    extends ProjectStagePhaseTemplateId, ReduxAction<typeof DELETE_PROJECT_STAGE_PHASE_TEMPLATE_SUCCESS> {}
export interface DeleteProjectStagePhaseTemplateErrorAction
    extends ErrorAction<typeof DELETE_PROJECT_STAGE_PHASE_TEMPLATE_ERROR> {}

export type DeleteProjectStagePhaseTemplateActionTypes =
    DeleteProjectStagePhaseTemplateAction
    | DeleteProjectStagePhaseTemplateSuccessAction
    | DeleteProjectStagePhaseTemplateErrorAction

// REDUX ACTION TYPE CONSTANTS
export const DELETE_PROJECT_STAGE_PHASE_TEMPLATE = "DELETE_PROJECT_STAGE_PHASE_TEMPLATE";
export const DELETE_PROJECT_STAGE_PHASE_TEMPLATE_SUCCESS = "DELETE_PROJECT_STAGE_PHASE_TEMPLATE_SUCCESS";
export const DELETE_PROJECT_STAGE_PHASE_TEMPLATE_ERROR = "DELETE_PROJECT_STAGE_PHASE_TEMPLATE_ERROR";

// REDUX ACTION ACCESSORS FUNCTIONS
export function deleteProjectStagePhaseTemplate(
    phaseTemplateId: Identifier<number>
): DeleteProjectStagePhaseTemplateAction {
    return { type: DELETE_PROJECT_STAGE_PHASE_TEMPLATE, phaseTemplateId };
}
export function deleteProjectStagePhaseTemplateSuccess(
    phaseTemplateId: Identifier<number>
): DeleteProjectStagePhaseTemplateSuccessAction {
    return { type: DELETE_PROJECT_STAGE_PHASE_TEMPLATE_SUCCESS, phaseTemplateId };
}
export function deleteProjectStagePhaseTemplateError(
    error: Error
): DeleteProjectStagePhaseTemplateErrorAction {
    return { type: DELETE_PROJECT_STAGE_PHASE_TEMPLATE_ERROR, error };
}

// REDUX OBSERVABLE EPIC
export function deleteProjectStagePhaseTemplateEpic(
    action$: Observable< DeleteProjectStagePhaseTemplateAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( DELETE_PROJECT_STAGE_PHASE_TEMPLATE ),
        mergeMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    pathWithId( action.phaseTemplateId.rawValue ),
                    RouteMethods.delete
                )
            ).pipe(
                mergeMap( _ => of(
                    deleteProjectStagePhaseTemplateSuccess( action.phaseTemplateId ),
                    eventNotificationSuccess(),
                    fetchProjectStagePhaseTemplateById( action.phaseTemplateId ),
                    fetchProjectStagePhaseTemplates( ProjectStagePhaseTemplateModule.identifier )
                ) ),
                catchError( error => of(
                    deleteProjectStagePhaseTemplateError( error ),
                    eventNotificationError(
                        error,
                        store$,
                        ProjectStagePhaseTemplateModule.identifier,
                        "deleteProjectStagePhaseTemplate"
                    )
                ) )
            )
        )
    );
}

/**
 * RESTORE PHASE TEMPLATE
 *
 * restores a previously soft deleted phase template
 */
export interface RestoreProjectStagePhaseTemplateAction
    extends ProjectStagePhaseTemplateId, ReduxAction<typeof RESTORE_PROJECT_STAGE_PHASE_TEMPLATE> {}
export interface RestoreProjectStagePhaseTemplateSuccessAction
    extends ProjectStagePhaseTemplateId, ReduxAction<typeof RESTORE_PROJECT_STAGE_PHASE_TEMPLATE_SUCCESS> {}
export interface RestoreProjectStagePhaseTemplateErrorAction
    extends ErrorAction<typeof RESTORE_PROJECT_STAGE_PHASE_TEMPLATE_ERROR> {}

export type RestoreProjectStagePhaseTemplateActionTypes =
    RestoreProjectStagePhaseTemplateAction
    | RestoreProjectStagePhaseTemplateSuccessAction
    | RestoreProjectStagePhaseTemplateErrorAction

// REDUX ACTION TYPE CONSTANTS
export const RESTORE_PROJECT_STAGE_PHASE_TEMPLATE = "RESTORE_PROJECT_STAGE_PHASE_TEMPLATE";
export const RESTORE_PROJECT_STAGE_PHASE_TEMPLATE_SUCCESS = "RESTORE_PROJECT_STAGE_PHASE_TEMPLATE_SUCCESS";
export const RESTORE_PROJECT_STAGE_PHASE_TEMPLATE_ERROR = "RESTORE_PROJECT_STAGE_PHASE_TEMPLATE_ERROR";

// REDUX ACTION ACCESSORS FUNCTIONS
export function restoreProjectStagePhaseTemplate(
    phaseTemplateId: Identifier<number>
): RestoreProjectStagePhaseTemplateAction {
    return { type: RESTORE_PROJECT_STAGE_PHASE_TEMPLATE, phaseTemplateId };
}
export function restoreProjectStagePhaseTemplateSuccess(
    phaseTemplateId: Identifier<number>
): RestoreProjectStagePhaseTemplateSuccessAction {
    return { type: RESTORE_PROJECT_STAGE_PHASE_TEMPLATE_SUCCESS, phaseTemplateId };
}
export function restoreProjectStagePhaseTemplateError(
    error: Error
): RestoreProjectStagePhaseTemplateErrorAction {
    return { type: RESTORE_PROJECT_STAGE_PHASE_TEMPLATE_ERROR, error };
}

// REDUX OBSERVABLE EPIC
export function restoreProjectStagePhaseTemplateEpic(
    action$: Observable< RestoreProjectStagePhaseTemplateAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( RESTORE_PROJECT_STAGE_PHASE_TEMPLATE ),
        mergeMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    pathWithId( action.phaseTemplateId.rawValue ),
                    RouteMethods.patch
                )
            ).pipe(
                mergeMap( _ => of(
                    restoreProjectStagePhaseTemplateSuccess( action.phaseTemplateId ),
                    eventNotificationSuccess(),
                    fetchProjectStagePhaseTemplateById( action.phaseTemplateId ),
                    fetchProjectStagePhaseTemplates( ProjectStagePhaseTemplateModule.identifier )
                ) ),
                catchError( error => of(
                    restoreProjectStagePhaseTemplateError( error ),
                    eventNotificationError(
                        error,
                        store$,
                        ProjectStagePhaseTemplateModule.identifier,
                        "restoreProjectStagePhaseTemplate"
                    )
                ) )
            )
        )
    );
}
